import React from 'react';
import { Settings, Trash2, Lightbulb, ChevronLeft, X, Plug } from 'lucide-react';
import type { TabsHeaderProps } from '../types';

/**
 * Sidebar header component
 * Desktop: Logo, title, new chat button, delete all button, capabilities, settings, collapse button
 * Mobile: Only icon and close button
 */
export const TabsHeader: React.FC<TabsHeaderProps> = React.memo(
  ({
    onChatHistoryClick,
    onCreateChat,
    onDeleteAllChats,
    hasChats,
    onOpenSettings,
    onOpenCapabilities,
    mcpConnectionsHref,
    onToggleCollapse,
    isCollapsed,
    onClose,
    meridianLogoUrl = '/meridian.png',
    forceDesktopHeader = false,
  }) => {
    const mobileHeaderClass = forceDesktopHeader
      ? 'hidden'
      : 'md:hidden p-3 border-b border-border bg-card dark:bg-card flex-shrink-0 flex items-center justify-between gap-2';
    const desktopHeaderClass = forceDesktopHeader
      ? 'block p-4 border-b border-border bg-card dark:bg-card flex-shrink-0'
      : 'hidden md:block p-4 border-b border-border bg-card dark:bg-card flex-shrink-0';

    return (
      <>
        {/* Mobile Header - Icon, new chat button, settings, and close button */}
        <div className={mobileHeaderClass}>
          <button
            type="button"
            onClick={onChatHistoryClick}
            className="flex items-center hover:opacity-80 transition-opacity cursor-pointer active:opacity-60"
            title="Go to chat history"
          >
            <img
              src={meridianLogoUrl}
              alt="Meridian"
              className="w-6 h-6 rounded-full object-cover"
            />
          </button>

          {/* Mobile action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {mcpConnectionsHref && (
              <a
                href={mcpConnectionsHref}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                title="MCP server connections"
                aria-label="MCP server connections"
              >
                <Plug className="w-5 h-5" />
              </a>
            )}
            {onOpenSettings && (
              <button
                type="button"
                onClick={onOpenSettings}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                title="Chat settings"
                aria-label="Chat settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            )}
            {onCreateChat && (
              <button
                type="button"
                onClick={onCreateChat}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                title="New chat"
                aria-label="New chat"
              >
                <span className="text-lg font-bold">+</span>
              </button>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                title="Close sidebar"
                aria-label="Close sidebar"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Desktop Header - Full content */}
        <div className={desktopHeaderClass}>
          <div className="mb-4">
            <div className="flex items-start gap-2 mb-1">
              {/* Logo and Title - takes remaining space */}
              <button
                type="button"
                onClick={onChatHistoryClick}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer min-w-0 flex-1 active:opacity-60"
                title="Go to chat history"
              >
                <img
                  src={meridianLogoUrl}
                  alt="Meridian"
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                />
                {!isCollapsed && (
                  <h2 className="text-lg font-bold text-foreground truncate">
                    Chat History
                  </h2>
                )}
              </button>

              {/* Desktop: Settings, Capabilities, Collapse buttons */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {mcpConnectionsHref && !isCollapsed && (
                  <a
                    href={mcpConnectionsHref}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                    title="MCP server connections"
                    aria-label="MCP server connections"
                  >
                    <Plug className="w-5 h-5" />
                  </a>
                )}
                {onOpenCapabilities && !isCollapsed && (
                  <button
                    type="button"
                    onClick={onOpenCapabilities}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                    title="What can Meridian do?"
                    aria-label="What can Meridian do?"
                  >
                    <Lightbulb className="w-5 h-5" />
                  </button>
                )}
                {onOpenSettings && !isCollapsed && (
                  <button
                    type="button"
                    onClick={onOpenSettings}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                    title="Chat settings"
                    aria-label="Chat settings"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                )}
                {onToggleCollapse && (
                  <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors active:opacity-60"
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  >
                    <ChevronLeft className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
            </div>
            {!isCollapsed && (
              <p className="text-xs text-muted-foreground">
                Your Conversations with Meridian
              </p>
            )}
          </div>
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCreateChat}
                className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 active:opacity-80 flex items-center justify-center gap-2"
              >
                <span>+</span>
                <span>New Chat</span>
              </button>
              {onDeleteAllChats && hasChats && (
                <button
                  type="button"
                  onClick={onDeleteAllChats}
                  className="p-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-300 active:opacity-60"
                  title="Delete all chats"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>
      </>
    );
  }
);

TabsHeader.displayName = 'TabsHeader';
